import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Info } from "lucide-react";
import { commissionsApi, type CommissionClaimDetailDto } from "@/api/commissions";
import { ApiError } from "@/api/client";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { currency, shortDate } from "@/components/commissions/formatters";
import { useDateFormatter } from "@/utils/datetime";

interface Props {
  open: boolean;
  onClose: () => void;
  claim: CommissionClaimDetailDto;
}

// SP-5 WS-3 — confirming revenue now means selecting the income that FUNDS the
// commission (an IncomeEvent linkage), not typing a date. The picker teaches
// the user what to pick, shows amount/date/source for each candidate, surfaces
// the selected-vs-basis delta (never blocking), and has an honest empty state.
export function ConfirmRevenueModal({ open, onClose, claim }: Props) {
  const { tz } = useDateFormatter();
  const qc = useQueryClient();
  const claimId = claim.summary.id;
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const candidates = useQuery({
    queryKey: commissionKeys.fundingCandidates(claimId),
    queryFn: () => commissionsApi.fundingCandidates(claimId),
    enabled: open,
  });

  const rows = candidates.data ?? [];
  const selectedIds = useMemo(
    () => rows.filter((r) => selected[r.incomeEventId]).map((r) => r.incomeEventId),
    [rows, selected],
  );
  const selectedTotal = useMemo(
    () => rows.filter((r) => selected[r.incomeEventId]).reduce((s, r) => s + r.amount, 0),
    [rows, selected],
  );
  const basis = claim.summary.grossRevenue;
  const delta = selectedTotal - basis;

  const mutation = useMutation({
    mutationFn: () =>
      commissionsApi.confirmRevenue(claimId, {
        incomeEventIds: selectedIds,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commissionKeys.all });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  const toggle = (id: string) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Confirm revenue — ${claim.summary.claimNumber}`}
      description="Select the income that funds this commission. Confirming marks the claim Payable and authorizes payout — Phoenix must have received the underlying revenue. Someone other than the payee must confirm, and a third person marks it paid."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={selectedIds.length === 0 || rows.length === 0}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
          >
            Confirm revenue
          </Button>
        </>
      }
    >
      {error && (
        <div role="alert" className="flex items-start gap-space-2 p-space-3 border border-status-error rounded-md bg-status-error/5 text-status-error text-small mb-space-3">
          <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
          <span>{error}</span>
        </div>
      )}

      {candidates.isLoading && (
        <div className="font-display text-label uppercase tracking-label text-gray-mid py-space-4">
          Finding matching income…
        </div>
      )}

      {!candidates.isLoading && rows.length === 0 && (
        <div className="flex items-start gap-space-2 p-space-4 border border-border-light rounded-md bg-white-warm text-small text-gray-mid">
          <Info size={16} strokeWidth={1.5} className="shrink-0 mt-[2px]" aria-hidden />
          <span>
            No matching income found — this commission can&rsquo;t be confirmed yet. Revenue
            confirmation requires a recognized, non-reversed Phoenix fee income event for this
            commission&rsquo;s pool
            {claim.summary.loanId ? " and loan" : ""}. It will appear here once the funding
            payment is recorded.
          </span>
        </div>
      )}

      {!candidates.isLoading && rows.length > 0 && (
        <div className="flex flex-col gap-space-3">
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
            Funding income
          </div>
          <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
            {rows.map((r) => (
              <li key={r.incomeEventId}>
                <label
                  className={
                    "flex items-center gap-space-3 p-space-3 border rounded-md cursor-pointer " +
                    (selected[r.incomeEventId]
                      ? "border-gold bg-gold/5"
                      : "border-border-light bg-white-pure")
                  }
                >
                  <input
                    type="checkbox"
                    checked={!!selected[r.incomeEventId]}
                    onChange={() => toggle(r.incomeEventId)}
                    className="accent-gold h-4 w-4"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-body text-onyx font-semibold tabular-nums">
                      {currency(r.amount)}{" "}
                      <span className="text-caption font-normal text-gray-mid">gross</span>
                    </div>
                    <div className="text-caption text-gray-mid">
                      {r.sourceLabel ?? r.sourceType} · recognized {shortDate(r.recognizedAt, tz)}
                    </div>
                  </div>
                  {r.alreadyLinked && (
                    <span className="font-display text-[10px] font-semibold uppercase tracking-label text-gray-mid">
                      Already linked
                    </span>
                  )}
                </label>
              </li>
            ))}
          </ul>

          {/* Selected-vs-basis delta — surfaced, never blocking (sibling role
              claims legitimately share the same gross income). */}
          <div className="p-space-3 border border-border-light rounded-md bg-white-warm text-small">
            <div className="flex justify-between">
              <span className="text-gray-mid">Selected income (gross)</span>
              <span className="tabular-nums font-semibold text-onyx">{currency(selectedTotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-mid">This commission&rsquo;s basis</span>
              <span className="tabular-nums text-onyx">{currency(basis)}</span>
            </div>
            {selectedIds.length > 0 && Math.abs(delta) >= 0.01 && (
              <div className="flex items-start gap-space-2 mt-space-2 text-caption text-gray-mid">
                <Info size={14} strokeWidth={1.5} className="shrink-0 mt-[2px]" aria-hidden />
                <span>
                  Selected income is {currency(Math.abs(delta))} {delta > 0 ? "more than" : "less than"}{" "}
                  this commission&rsquo;s basis. That&rsquo;s expected when one income event funds
                  several role commissions, or when a fee is only partly commissionable — it does
                  not block confirmation.
                </span>
              </div>
            )}
          </div>

          <label className="flex flex-col gap-space-1">
            <span className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
              Notes (optional)
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-space-3 py-space-2 border border-border-light rounded-md font-body text-small bg-white-pure focus:border-gold focus:outline-none"
            />
          </label>
        </div>
      )}
    </Modal>
  );
}
